import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  MEALS,
  MEAL_EMOJI,
  MEAL_TITLE,
  formatFullDate,
  formatNumber,
  formatDecimal,
  weekdayShort,
  dayOfMonth,
  type DayNutrition,
  type FoodEntry,
  type Meal,
} from '@jungles/shared';
import { api } from '../lib/api';
import { useApi } from '../lib/hooks';
import { useToast } from '../app/toast';
import { haptic } from '../lib/telegram';
import {
  ErrorState,
  Header,
  JungleStrip,
  Loader,
  Screen,
  TabBar,
} from '../components/Chrome';
import { ChevronIcon } from '../components/icons';
import { FoodPicker } from '../components/FoodPicker';

/** N4 · Дневник питания (SPEC §5). Будущие дни недоступны. */

interface DayResponse {
  today: string;
  day: DayNutrition;
  week: { date: string; isToday: boolean; isFuture: boolean; hasFood: boolean }[];
}

/** Смахивание влево открывает красную кнопку «Удалить». */
function useSwipe(onOpen: () => void, onClose: () => void) {
  const startX = useRef<number | null>(null);
  return {
    onTouchStart: (event: React.TouchEvent) => {
      startX.current = event.touches[0]?.clientX ?? null;
    },
    onTouchEnd: (event: React.TouchEvent) => {
      if (startX.current === null) return;
      const delta = (event.changedTouches[0]?.clientX ?? 0) - startX.current;
      if (delta < -40) onOpen();
      else if (delta > 20) onClose();
      startX.current = null;
    },
  };
}

function Macros({ p, f, c }: Pick<FoodEntry, 'p' | 'f' | 'c'>) {
  return <span className="diary-macros">
    <span>Б <b>{formatDecimal(p)}</b></span><span aria-hidden="true">·</span>
    <span>Ж <b>{formatDecimal(f)}</b></span><span aria-hidden="true">·</span>
    <span>У <b>{formatDecimal(c)}</b></span>
  </span>;
}

function EntryRow({
  entry,
  onEdit,
  onDelete,
}: {
  entry: FoodEntry;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const swipe = useSwipe(
    () => setOpen(true),
    () => setOpen(false),
  );

  return (
    <li className={open ? 'it sw' : 'it'} {...swipe}>
      <button className="del" type="button" onClick={onDelete}>
        Удалить
      </button>
      <button
        className="it-row"
        type="button"
        onClick={() => (open ? setOpen(false) : onEdit())}
      >
        <span className="nm">
          <b>{entry.name}</b>
          <small className="diary-entry-meta">
            <span>{formatDecimal(entry.grams)} {entry.quantityUnit === 'ml' ? 'мл' : 'г'}</span>
            <Macros {...entry} />
            {entry.brand ? <span className="diary-entry-brand">{entry.brand}</span> : null}
          </small>
        </span>
        <span className="kcal">{formatNumber(entry.kcal)} ккал</span>
        <ChevronIcon className="chev" />
      </button>
    </li>
  );
}

export function NutritionScreen() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  const dateParam = params.get('date');
  const { data, error, loading, reload } = useApi<DayResponse>(
    `/api/nutrition/day${dateParam ? `?date=${dateParam}` : ''}`,
  );
  const [picker, setPicker] = useState<Meal | null>(null);

  const selectDay = (date: string) => {
    haptic();
    setParams({ date });
  };

  const removeEntry = async (entry: FoodEntry) => {
    try {
      await api.del(`/api/nutrition/entries/${entry.id}`);
      toast('Запись удалена');
      reload();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Не удалось удалить');
    }
  };

  if (loading) {
    return (
      <Screen tabs>
        <Header />
        <Loader />
        <TabBar />
      </Screen>
    );
  }

  if (error || !data) {
    return (
      <Screen tabs>
        <Header />
        <ErrorState message={error ?? 'Нет данных'} onRetry={reload} />
        <TabBar />
      </Screen>
    );
  }

  const { day, week } = data;

  return (
    <Screen tabs>
      <Header />
      <h1>Питание</h1>

      <nav className="wkn" aria-label="День недели">
        {week.map((item) => (
          <button
            key={item.date}
            type="button"
            className={`dayn${item.date === day.date ? ' on' : ''}${item.isFuture ? ' fut' : ''}`}
            disabled={item.isFuture}
            aria-current={item.date === day.date ? 'date' : undefined}
            onClick={() => selectDay(item.date)}
          >
            {weekdayShort(item.date)}
            <b>{dayOfMonth(item.date)}</b>
            <i className={item.hasFood ? 'food' : undefined} />
          </button>
        ))}
      </nav>

      <section className="card tight">
        <div className="row">
          <b>{formatFullDate(day.date)}</b>
          <button
            className="back"
            type="button"
            style={{ minHeight: 'auto', padding: 0, fontSize: 14 }}
            onClick={() => navigate('/nutrition/library')}
          >
            Библиотека
          </button>
        </div>
        <div className="tot" style={{ marginTop: 8 }}>
          {formatNumber(day.kcal)} <small>ккал</small>
        </div>
        <div className="mac diary-day-macros">
          <span>
            Б <b>{formatDecimal(day.p)}</b> г
          </span>
          <span>
            Ж <b>{formatDecimal(day.f)}</b> г
          </span>
          <span>
            У <b>{formatDecimal(day.c)}</b> г
          </span>
        </div>
      </section>

      {MEALS.map((meal) => {
        const bucket = day.meals[meal];
        // Entry DTOs already contain the saved portion, not per-100 nutrients.
        const macros = bucket.entries.reduce((sum, entry) => ({
          p: sum.p + entry.p, f: sum.f + entry.f, c: sum.c + entry.c,
        }), { p: 0, f: 0, c: 0 });
        return (
          <section className="card tight" key={meal}>
            <div className="meal">
              <span className="ico emo" aria-hidden="true">
                {MEAL_EMOJI[meal]}
              </span>
              <div>
                <h3>{MEAL_TITLE[meal]}</h3>
                <div className="kc diary-meal-meta"><span>{formatNumber(bucket.kcal)} ккал</span><Macros {...macros} /></div>
              </div>
              <button
                className="add"
                type="button"
                onClick={() => setPicker(meal)}
                aria-label={`Добавить в «${MEAL_TITLE[meal]}»`}
              >
                +
              </button>
            </div>

            {bucket.entries.length === 0 ? (
              <p className="empty">Пока ничего не записано</p>
            ) : (
              <ul className="items">
                {bucket.entries.map((entry) => (
                  <EntryRow
                    key={entry.id}
                    entry={entry}
                    onEdit={() =>
                      navigate(
                        `/nutrition/food?entry=${entry.id}&meal=${meal}&date=${day.date}`,
                      )
                    }
                    onDelete={() => void removeEntry(entry)}
                  />
                ))}
              </ul>
            )}
          </section>
        );
      })}

      <JungleStrip />
      <TabBar />

      {picker ? (
        <FoodPicker meal={picker} date={day.date} onClose={() => setPicker(null)} />
      ) : null}
    </Screen>
  );
}
